import {
  all,
  call,
  put,
  takeEvery,
  takeLatest,
  takeLeading
} from 'redux-saga/effects';

import { customerId } from 'common/helpers/tokenHelper';
import { addAPIError, removeAPIError } from 'redux/apiErrors/sagas';
import { LIST_CLUB_MEMBER_SHIPMENTS_SUCCESS } from 'redux/clubMemberShipments/constants';
import { getVirtualCart as getVirtualCartForTraditionalShipment } from 'redux/clubMemberShipments/sagas';
import { LIST_CLUB_PACKAGES_SUCCESS } from 'redux/clubPackages/constants';
import { LIST_CLUB_SUBSCRIPTION_SHIPMENTS_SUCCESS } from 'redux/clubSubscriptionShipments/constants';
import { getVirtualCart as getVirtualCartForSubscriptionShipment } from 'redux/clubSubscriptionShipments/sagas';
import { LIST_CLUBS_SUCCESS } from 'redux/clubs/constants';

import {
  addClubMembershipHold,
  cancelClubMembership,
  editClubMembership,
  editClubMembershipHold,
  getClubMembership,
  getClubMembershipV2,
  getClubPackage,
  getNextShipment,
  listClubMembershipsV2
} from './api';
import {
  ADD_CLUB_MEMBERSHIP_HOLD_REQUEST,
  CANCEL_CLUB_MEMBERSHIP_REQUEST,
  CLUB_MEMBERSHIP_LIST_REQUESTING,
  CLUB_MEMBERSHIP_OBJECT_REQUESTING,
  EDIT_CLUB_MEMBERSHIP_HOLD_REQUEST,
  EDIT_CLUB_MEMBERSHIP_REQUEST,
  GET_CLUB_MEMBERSHIP_REQUEST,
  GET_CLUB_MEMBERSHIP_SUCCESS,
  GET_CLUB_MEMBERSHIP_SUCCESSV2,
  LIST_CLUB_MEMBERSHIPS_REQUEST,
  LIST_CLUB_MEMBERSHIPS_SUCCESS,
  LIST_CLUB_MEMBERSHIPS_SUCCESSV2,
  RESET_CLUB_MEMBERSHIP_ISFETCHING
} from './constants';

function* list(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: CLUB_MEMBERSHIP_LIST_REQUESTING });

    const v2Response = yield call(
      listClubMembershipsV2,
      customerId(),
      action.queryString
    );
    yield call(listPayloadV2, v2Response.data.data, customerId());
  } catch (error) {
    yield put({ type: RESET_CLUB_MEMBERSHIP_ISFETCHING });
    yield call(addAPIError, error);
  }
}

export function* listPayload(payload) {
  yield call(updateClubPackages, payload.clubMemberships);
  yield call(updateClubMemberShipments, payload.clubMemberships);
  yield call(updateClubSubscriptionShipments, payload.clubMemberships);
  yield call(updateClubs, payload.clubMemberships);

  payload.clubMemberships = payload.clubMemberships.map((clubMembership) => {
    const newMembership = transformMembership(clubMembership);
    return newMembership;
  });

  yield put({
    type: LIST_CLUB_MEMBERSHIPS_SUCCESS,
    payload
  });

  // TODO - Yalcin temporarily keeping this for backward compatibility
  // Remove this after personalization-block  is updated to use V2
  yield put({
    type: LIST_CLUB_MEMBERSHIPS_SUCCESSV2,
    payload: {
      data: payload.clubMemberships,
      total: payload.total
    }
  });
}

function* listPayloadV2(payloadV2, customerId) {
  const traditionalClubNextShipment = yield call(
    listNextTraditionalClubShipments,
    payloadV2,
    customerId
  );
  const subscriptionClubNextShipment = yield call(
    listNextSubscriptionClubShipments,
    payloadV2,
    customerId
  );
  yield call(updateClubs, payloadV2);

  const transformedData = yield call(
    transformDataForMembership,
    payloadV2,
    traditionalClubNextShipment.clubPackageMap,
    traditionalClubNextShipment.clubMemberShipmentsMap,
    subscriptionClubNextShipment.clubSubscriptionShipmentMap
  );

  yield put({
    type: LIST_CLUB_MEMBERSHIPS_SUCCESSV2,
    payload: transformedData
  });
}

function* transformDataForMembership(
  clubMemberships,
  clubPackageMap,
  clubMemberShipmentsMap,
  clubSubscriptionShipmentMap
) {
  const transformClubMemberships = clubMemberships.map((clubMembership) => {
    if (clubPackageMap[clubMembership.id]) {
      clubMembership.activeClubPackageId = clubPackageMap[clubMembership.id];
    }
    if (clubMemberShipmentsMap[clubMembership.id]) {
      clubMembership.activeClubMemberShipmentId =
        clubMemberShipmentsMap[clubMembership.id];
    }
    if (clubSubscriptionShipmentMap[clubMembership.id]) {
      clubMembership.activeClubSubscriptionShipmentId =
        clubSubscriptionShipmentMap[clubMembership.id];
    }
    return clubMembership;
  });
  return {
    data: transformClubMemberships,
    total: clubMemberships.total
  };
}

function* listNextTraditionalClubShipments(clubMemberships, customerId) {
  const clubMemberShipments = [];
  const clubPackages = [];
  const clubPackageMap = {};
  const clubMemberShipmentsMap = {};
  const activeTraditionalMembershipsForCustomer = clubMemberships.filter(
    (clubMembership) =>
      clubMembership.status === 'Active' &&
      clubMembership.club.type === 'Traditional'
  );

  for (const clubMembership of activeTraditionalMembershipsForCustomer) {
    const response = yield call(getNextShipment, customerId, clubMembership.id);

    if (response.data.clubMemberShipment) {
      clubMemberShipments.push(response.data.clubMemberShipment);
      clubMemberShipmentsMap[clubMembership.id] =
        response.data.clubMemberShipment.id;
    }
    if (
      response.data.clubMemberShipment &&
      response.data.clubMemberShipment.clubPackageId
    ) {
      const clubPackageId = response.data.clubMemberShipment.clubPackageId;
      clubPackageMap[clubMembership.id] = clubPackageId;

      const clubPackageResponse = yield call(getClubPackage, clubPackageId);
      if (clubPackageResponse.data) {
        clubPackages.push(clubPackageResponse.data);
      }
    }
  }

  if (clubMemberShipments.length > 0) {
    yield put({
      type: LIST_CLUB_MEMBER_SHIPMENTS_SUCCESS,
      payload: { clubMemberShipments }
    });
  }

  yield put({
    type: LIST_CLUB_PACKAGES_SUCCESS,
    payload: { clubPackages }
  });

  return { clubPackageMap, clubMemberShipmentsMap };
}

function* listNextSubscriptionClubShipments(clubMemberships, customerId) {
  const clubSubscriptionShipments = [];
  const clubSubscriptionShipmentMap = {};
  const activeSubscriptionMembershipsForCustomer = clubMemberships.filter(
    (clubMembership) =>
      clubMembership.status === 'Active' &&
      clubMembership.club.type === 'Subscription'
  );

  for (const clubMembership of activeSubscriptionMembershipsForCustomer) {
    const response = yield call(getNextShipment, customerId, clubMembership.id);
    if (response.data.clubSubscriptionShipment) {
      clubSubscriptionShipmentMap[clubMembership.id] =
        response.data.clubSubscriptionShipment.id;
      clubSubscriptionShipments.push(response.data.clubSubscriptionShipment);
    }
  }

  if (clubSubscriptionShipments.length > 0) {
    yield put({
      type: LIST_CLUB_SUBSCRIPTION_SHIPMENTS_SUCCESS,
      payload: { clubSubscriptionShipments }
    });
  }
  return { clubSubscriptionShipmentMap };
}

function* updateClubPackages(clubMemberships) {
  const clubPackages = [];
  clubMemberships.forEach((clubMembership) => {
    if (clubMembership.clubPackage) {
      clubPackages.push(clubMembership.clubPackage);
    }
  });
  yield put({
    type: LIST_CLUB_PACKAGES_SUCCESS,
    payload: { clubPackages }
  });
}

function* updateClubs(clubMemberships) {
  const clubs = [];
  clubMemberships.forEach((clubMembership) => {
    if (clubMembership.club) {
      clubs.push(clubMembership.club);
    }
  });
  yield put({
    type: LIST_CLUBS_SUCCESS,
    payload: { data: clubs }
  });
}

function* updateClubMemberShipments(clubMemberships) {
  const clubMemberShipments = [];
  clubMemberships.forEach((clubMembership) => {
    if (clubMembership.clubMemberShipment) {
      clubMemberShipments.push(clubMembership.clubMemberShipment);
    }
  });
  yield put({
    type: LIST_CLUB_MEMBER_SHIPMENTS_SUCCESS,
    payload: { clubMemberShipments }
  });
}

function* updateClubSubscriptionShipments(clubMemberships) {
  const clubSubscriptionShipments = [];
  clubMemberships.forEach((clubMembership) => {
    if (clubMembership.clubSubscriptionShipment) {
      clubSubscriptionShipments.push(clubMembership.clubSubscriptionShipment);
    }
  });
  yield put({
    type: LIST_CLUB_SUBSCRIPTION_SHIPMENTS_SUCCESS,
    payload: { clubSubscriptionShipments }
  });
}

function* get(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: CLUB_MEMBERSHIP_OBJECT_REQUESTING });
    const [v1Response, v2Response] = yield all([
      call(getClubMembership, action.clubMembershipId),
      call(getClubMembershipV2, action.clubMembershipId, customerId())
    ]);
    yield call(updateClubs, [v2Response.data]);
    const traditionalClubNextShipment = yield call(
      listNextTraditionalClubShipments,
      [v2Response.data],
      customerId()
    );
    const subscriptionClubNextShipment = yield call(
      listNextSubscriptionClubShipments,
      [v2Response.data],
      customerId()
    );

    const transformedData = yield call(
      transformDataForMembership,
      [v2Response.data],
      traditionalClubNextShipment.clubPackageMap,
      traditionalClubNextShipment.clubMemberShipmentsMap,
      subscriptionClubNextShipment.clubSubscriptionShipmentMap
    );

    const newMembership = transformMembership(v1Response.data);
    yield put({
      type: GET_CLUB_MEMBERSHIP_SUCCESS,
      payload: newMembership
    });
    yield put({
      type: GET_CLUB_MEMBERSHIP_SUCCESSV2,
      payload: transformedData.data[0]
    });
    return newMembership;
  } catch (error) {
    yield put({ type: RESET_CLUB_MEMBERSHIP_ISFETCHING });
    yield call(addAPIError, error);
    return null;
  }
}

function* edit(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: CLUB_MEMBERSHIP_OBJECT_REQUESTING });
    yield call(
      editClubMembership,
      customerId(),
      action.clubMembershipId,
      action.data
    );
    const newMembership = yield call(get, {
      clubMembershipId: action.clubMembershipId
    });

    if (action.onSuccess) {
      // get virtual cart if it is subscription shipment
      if (newMembership?.activeClubSubscriptionShipmentId) {
        yield call(getVirtualCartForSubscriptionShipment, {
          clubSubscriptionShipmentId:
            newMembership.activeClubSubscriptionShipmentId
        });
      }
      // get virtual cart if it is traditional shipment
      if (newMembership?.activeClubMemberShipmentId) {
        yield call(getVirtualCartForTraditionalShipment, {
          clubMemberShipmentId: newMembership.activeClubMemberShipmentId
        });
      }

      action.onSuccess(newMembership);
    }
  } catch (error) {
    yield put({ type: RESET_CLUB_MEMBERSHIP_ISFETCHING });
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError(error);
    }
  }
}

function* cancel(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: CLUB_MEMBERSHIP_OBJECT_REQUESTING });
    yield call(
      cancelClubMembership,
      customerId(),
      action.clubMembershipId,
      action.data
    );
    const newMembership = yield call(get, {
      clubMembershipId: action.clubMembershipId
    });

    if (action.onSuccess) {
      action.onSuccess(newMembership);
    }
  } catch (error) {
    yield put({ type: RESET_CLUB_MEMBERSHIP_ISFETCHING });
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError(error);
    }
  }
}

function* addHold(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: CLUB_MEMBERSHIP_OBJECT_REQUESTING });
    yield call(
      addClubMembershipHold,
      customerId(),
      action.clubMembershipId,
      action.data
    );
    const newMembership = yield call(get, {
      clubMembershipId: action.clubMembershipId
    });
    if (action.onSuccess) {
      action.onSuccess(newMembership);
    }
  } catch (error) {
    yield put({ type: RESET_CLUB_MEMBERSHIP_ISFETCHING });
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError(error);
    }
  }
}

const transformMembership = (clubMembership) => {
  const newMembership = clubMembership;
  if (newMembership.clubPackage) {
    newMembership.activeClubPackageId = newMembership.clubPackage.id;
    delete newMembership.clubPackage;
  }
  if (newMembership.clubMemberShipment) {
    newMembership.activeClubMemberShipmentId =
      newMembership.clubMemberShipment.id;
    delete newMembership.clubMemberShipment;
  }
  if (newMembership.clubSubscriptionShipment) {
    newMembership.activeClubSubscriptionShipmentId =
      newMembership.clubSubscriptionShipment.id;
    delete newMembership.clubSubscriptionShipment;
  }
  return newMembership;
};

function* editHold(action) {
  try {
    yield call(removeAPIError);
    yield call(
      editClubMembershipHold,
      customerId(),
      action.clubMembershipId,
      action.id,
      action.data
    );
    const newMembership = yield call(get, {
      clubMembershipId: action.clubMembershipId
    });
    if (action.onSuccess) {
      action.onSuccess(newMembership);
    }
  } catch (error) {
    yield put({ type: RESET_CLUB_MEMBERSHIP_ISFETCHING });
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError(error);
    }
  }
}

function* watchList() {
  yield takeLeading(LIST_CLUB_MEMBERSHIPS_REQUEST, list);
  yield takeLatest(GET_CLUB_MEMBERSHIP_REQUEST, get);
  yield takeEvery(EDIT_CLUB_MEMBERSHIP_REQUEST, edit);
  yield takeLatest(CANCEL_CLUB_MEMBERSHIP_REQUEST, cancel);
  yield takeLatest(ADD_CLUB_MEMBERSHIP_HOLD_REQUEST, addHold);
  yield takeLatest(EDIT_CLUB_MEMBERSHIP_HOLD_REQUEST, editHold);
}

export default function* clubMembershipSagas() {
  yield all([watchList()]);
}
